import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glow, haze, glowColor, bake, beam, prism, Gait } from './units-style.js';

// Hips of the walker's four legs. Every part of both models stays within 0.95 of
// the server collision radius (2.0 tank, 2.5 walker) at any pose or turret angle.
const HIP_Y = 1.75;
const HIPS = [[-1, 1], [1, 1], [-1, -1], [1, -1]];

// Hover Tank: a wedge hull floating on four glowing hover pads, with a
// twin-barrel energy cannon. The Siege Walker (super tank) is a heavy
// four-legged mech with a long rail cannon and gold and white trim.
export class Tank {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.turretGroup = null; // Separate group for turret rotation
    this.hoverGroup = null; // Hull and turret, bobbing on the hover field
    this.legs = [];
    this.geometries = [];
    this.lastPosition = null;
    this.targetRotation = 0;
    this.turretTargetRotation = 0;
    this.currentTurretRotation = 0;
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperTankHealth vs TankHealth
    this.phase = Math.random() * Math.PI * 2; // So squads don't bob in unison
    this.lean = 0;
    this.recoil = 0;
    this.create();
  }

  create() {
    // Scale factor for super tanks (1.3x larger)
    const scale = this.isSuper ? 1.3 : 1.0;

    this.mesh = new THREE.Group();

    // The model is built with the ground at y=0, which is where the unit stands
    const model = new THREE.Group();
    model.scale.setScalar(scale);
    this.mesh.add(model);

    this.hoverGroup = new THREE.Group();
    model.add(this.hoverGroup);

    const build = this.isSuper ? this.buildWalker() : this.buildHull();
    this.addParts(this.hoverGroup, build.hull, build.glow);

    // Create turret group for independent rotation
    this.turretGroup = new THREE.Group();
    this.turretGroup.position.y = build.turretY;
    this.hoverGroup.add(this.turretGroup);
    // The gun kicks back inside the turret group when it fires
    this.recoilGroup = new THREE.Group();
    this.turretGroup.add(this.recoilGroup);
    const turret = this.isSuper ? this.buildCannon() : this.buildTwinCannon();
    this.addParts(this.recoilGroup, turret.hull, turret.glow);

    if (this.isSuper) {
      // Legs hang off the model, not the bobbing hull, so the feet stay planted
      for (const [sx, sz] of HIPS) {
        const leg = new THREE.Group();
        leg.position.set(sx * 0.75, HIP_Y, sz * 0.8);
        leg.userData = { sx, offset: sx * sz > 0 ? 0 : Math.PI };
        const parts = this.buildLeg(sx, sz);
        this.addParts(leg, parts.hull, parts.glow);
        model.add(leg);
        this.legs.push(leg);
      }
    } else {
      // Pool of light thrown on the ground by the hover field
      const wash = new THREE.Mesh(this.track(new THREE.CircleGeometry(1.6, 16)), haze(glowColor(this.color, 0.05)));
      wash.rotation.x = -Math.PI / 2;
      wash.scale.set(0.85, 1.1, 1);
      wash.position.y = 0.04;
      wash.renderOrder = 1;
      model.add(wash);
      this.wash = wash;
    }

    // Create health bar above tank
    this.healthBar = new HealthBar(this.maxHealth, 3 * scale, 0.35);
    this.healthBar.getGroup().position.y = this.isSuper ? 4.9 : 3.5;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.gait = new Gait(this.mesh);

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  // An armour mesh plus its unlit glow trim, added to a group
  addParts(group, hullParts, glowParts) {
    const hull = new THREE.Mesh(this.track(bake(hullParts)), armor());
    hull.castShadow = true;
    hull.receiveShadow = true;
    group.add(hull);
    if (glowParts.length) {
      group.add(new THREE.Mesh(this.track(bake(glowParts)), glow()));
    }
  }

  // Hover tank hull, facing +Z
  buildHull() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [];
    const glowParts = [];

    // Hover pads at the corners, each with a glowing emitter underneath
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        hull.push(colored(new THREE.BoxGeometry(0.56, 0.32, 0.8), SCIFI.gunmetal, { x: sx * 0.92, y: 0.5, z: sz * 0.92 }));
        hull.push(colored(new THREE.BoxGeometry(0.6, 0.08, 0.84), SCIFI.alloyDark, { x: sx * 0.92, y: 0.68, z: sz * 0.92 }));
        glowParts.push(colored(new THREE.BoxGeometry(0.44, 0.04, 0.66), trim, { x: sx * 0.92, y: 0.33, z: sz * 0.92 }));
      }
    }

    // Gunmetal lower hull and a sloped team-coloured upper hull
    hull.push(colored(prism([[-1.45, 0.5], [1.1, 0.5], [1.48, 0.92], [-1.5, 0.92]], 2.0), SCIFI.gunmetal));
    hull.push(colored(prism([[-1.5, 0.92], [1.48, 0.92], [0.9, 1.42], [-1.3, 1.48]], 1.84), team));
    // Pale alloy cheek armour along the sides
    for (const sx of [-1, 1]) {
      hull.push(colored(new THREE.BoxGeometry(0.14, 0.5, 2.3), SCIFI.alloy, { x: sx * 0.98, y: 1.05, z: -0.1, rz: sx * 0.28 }));
      hull.push(colored(new THREE.BoxGeometry(0.16, 0.08, 2.32), SCIFI.white, { x: sx * 1.03, y: 1.3, z: -0.1, rz: sx * 0.28 }));
    }
    // Panel lines across the deck and a raised spine
    for (const z of [-0.85, 0.15]) {
      hull.push(colored(new THREE.BoxGeometry(1.86, 0.03, 0.06), SCIFI.black, { y: 1.47, z }));
    }
    hull.push(colored(new THREE.BoxGeometry(0.3, 0.08, 1.2), SCIFI.alloy, { y: 1.5, z: -0.85 }));
    // Rear engine block
    hull.push(colored(new THREE.BoxGeometry(1.5, 0.5, 0.2), SCIFI.alloyDark, { y: 1.05, z: -1.55 }));

    // Glowing trim: side strips, engine vents and cyan headlights
    for (const sx of [-1, 1]) {
      glowParts.push(colored(new THREE.BoxGeometry(0.05, 0.07, 2.6), trim, { x: sx * 1.01, y: 0.74, z: 0.0 }));
      glowParts.push(colored(new THREE.BoxGeometry(0.28, 0.1, 0.06), SCIFI.cyan, { x: sx * 0.55, y: 0.8, z: 1.5, rx: -0.6 }));
    }
    for (const y of [0.92, 1.12]) {
      glowParts.push(colored(new THREE.BoxGeometry(1.2, 0.07, 0.04), trim, { y, z: -1.66 }));
    }

    return { hull, glow: glowParts, turretY: 1.46 };
  }

  // Twin-barrel energy cannon aimed along local +Z, the direction turretGroup rotation faces
  buildTwinCannon() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [
      colored(new THREE.CylinderGeometry(0.82, 0.9, 0.22, 6), SCIFI.alloyDark, { y: 0.11 }),
      colored(prism([[-0.85, 0.2], [0.75, 0.2], [1.0, 0.45], [0.55, 0.8], [-0.7, 0.82]], 1.3), team),
      colored(new THREE.BoxGeometry(0.14, 0.5, 1.2), SCIFI.white, { x: -0.68, y: 0.5, z: 0.05 }),
      colored(new THREE.BoxGeometry(0.14, 0.5, 1.2), SCIFI.white, { x: 0.68, y: 0.5, z: 0.05 }),
      colored(new THREE.BoxGeometry(1.32, 0.03, 0.05), SCIFI.black, { y: 0.83, z: -0.15 }),
      // Sensor block and antenna
      colored(new THREE.BoxGeometry(0.34, 0.24, 0.4), SCIFI.gunmetal, { x: -0.35, y: 0.94, z: -0.1 }),
      colored(new THREE.CylinderGeometry(0.025, 0.025, 0.8, 4), SCIFI.alloy, { x: 0.45, y: 1.2, z: -0.6 })
    ];
    const glowParts = [
      colored(new THREE.BoxGeometry(0.2, 0.08, 0.04), SCIFI.cyan, { x: -0.35, y: 0.96, z: 0.11 }),
      colored(new THREE.CylinderGeometry(0.91, 0.91, 0.05, 6), trim, { y: 0.2 }),
      colored(new THREE.SphereGeometry(0.06, 4, 3), trim, { x: 0.45, y: 1.62, z: -0.6 })
    ];
    for (const sx of [-1, 1]) {
      const x = sx * 0.24;
      hull.push(colored(new THREE.BoxGeometry(0.26, 0.26, 0.5), SCIFI.gunmetal, { x, y: 0.48, z: 0.9 }));
      hull.push(colored(new THREE.CylinderGeometry(0.075, 0.09, 0.65, 6), SCIFI.alloyDark, { x, y: 0.48, z: 1.3, rx: Math.PI / 2 }));
      // Coil rings and muzzle
      for (const z of [1.2, 1.42]) {
        glowParts.push(colored(new THREE.CylinderGeometry(0.12, 0.12, 0.07, 6), trim, { x, y: 0.48, z, rx: Math.PI / 2 }));
      }
      hull.push(colored(new THREE.CylinderGeometry(0.13, 0.11, 0.16, 6), SCIFI.gunmetal, { x, y: 0.48, z: 1.62, rx: Math.PI / 2 }));
      glowParts.push(colored(new THREE.CylinderGeometry(0.08, 0.08, 0.04, 6), trim, { x, y: 0.48, z: 1.71, rx: Math.PI / 2 }));
    }
    return { hull, glow: glowParts };
  }

  // Siege walker body: an armoured cab carried on four legs
  buildWalker() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [
      // Gunmetal belly and hip frame
      colored(prism([[-1.2, 1.35], [1.1, 1.35], [1.4, 1.85], [-1.3, 1.95]], 1.7), SCIFI.gunmetal),
      colored(new THREE.BoxGeometry(1.9, 0.3, 2.0), SCIFI.alloyDark, { y: HIP_Y }),
      // Team armour shell with white flank plates and gold edging
      colored(prism([[-1.3, 1.9], [1.42, 1.85], [0.95, 2.5], [-1.1, 2.58]], 1.9), team),
      colored(new THREE.BoxGeometry(1.94, 0.06, 0.08), SCIFI.gold, { y: 2.55, z: -1.08 }),
      colored(new THREE.BoxGeometry(1.94, 0.06, 0.08), SCIFI.gold, { y: 2.5, z: 0.94 }),
      colored(new THREE.BoxGeometry(0.3, 0.06, 2.0), SCIFI.white, { y: 2.6, z: -0.05 }),
      // Armoured chin and sensor cluster
      colored(new THREE.BoxGeometry(0.9, 0.35, 0.3), SCIFI.alloy, { y: 1.8, z: 1.42, rx: 0.4 }),
      // Rear reactor stack
      colored(new THREE.BoxGeometry(1.2, 0.7, 0.4), SCIFI.alloyDark, { y: 2.2, z: -1.38 }),
      colored(new THREE.CylinderGeometry(0.18, 0.2, 0.6, 6), SCIFI.gunmetal, { x: -0.4, y: 2.75, z: -1.38 }),
      colored(new THREE.CylinderGeometry(0.18, 0.2, 0.6, 6), SCIFI.gunmetal, { x: 0.4, y: 2.75, z: -1.38 })
    ];
    const glowParts = [
      colored(new THREE.BoxGeometry(0.7, 0.08, 0.05), SCIFI.cyan, { y: 1.9, z: 1.56, rx: 0.4 }),
      colored(new THREE.BoxGeometry(1.0, 0.12, 0.04), trim, { y: 2.2, z: -1.59 }),
      colored(new THREE.CylinderGeometry(0.13, 0.13, 0.05, 6), trim, { x: -0.4, y: 3.06, z: -1.38 }),
      colored(new THREE.CylinderGeometry(0.13, 0.13, 0.05, 6), trim, { x: 0.4, y: 3.06, z: -1.38 })
    ];
    for (const sx of [-1, 1]) {
      hull.push(colored(new THREE.BoxGeometry(0.16, 0.62, 2.2), SCIFI.white, { x: sx * 0.98, y: 2.12, z: -0.05, rz: sx * 0.22 }));
      hull.push(colored(new THREE.BoxGeometry(0.18, 0.06, 2.22), SCIFI.gold, { x: sx * 1.04, y: 2.42, z: -0.05, rz: sx * 0.22 }));
      glowParts.push(colored(new THREE.BoxGeometry(0.05, 0.07, 2.0), trim, { x: sx * 1.08, y: 1.98, z: -0.05, rz: sx * 0.22 }));
    }
    return { hull, glow: glowParts, turretY: 2.55 };
  }

  // One spider leg, outward along sx; the hip is the group origin
  buildLeg(sx, sz) {
    const team = this.color;
    const knee = [sx * 0.45, 0.4, sz * 0.1];
    const foot = [sx * 0.35, -HIP_Y + 0.12, sz * 0.2];
    const hull = [
      colored(new THREE.SphereGeometry(0.3, 6, 4), SCIFI.gunmetal, {}),
      beam([0, 0, 0], knee, 0.34, SCIFI.white),
      beam([sx * 0.05, 0.18, 0], [knee[0], knee[1] + 0.18, knee[2]], 0.12, team),
      colored(new THREE.SphereGeometry(0.24, 6, 4), SCIFI.gold, { x: knee[0], y: knee[1], z: knee[2] }),
      beam(knee, foot, 0.32, SCIFI.alloyDark),
      colored(new THREE.CylinderGeometry(0.24, 0.3, 0.22, 6), SCIFI.gunmetal, { x: foot[0], y: foot[1], z: foot[2] })
    ];
    const glowParts = [
      colored(new THREE.CylinderGeometry(0.31, 0.31, 0.04, 6), glowColor(team), { x: foot[0], y: foot[1] - 0.05, z: foot[2] })
    ];
    return { hull, glow: glowParts };
  }

  // Heavy rail cannon on the walker, aimed along local +Z
  buildCannon() {
    const team = this.color;
    const trim = glowColor(team);
    const hull = [
      colored(new THREE.CylinderGeometry(0.95, 1.05, 0.25, 6), SCIFI.gunmetal, { y: 0.12 }),
      colored(prism([[-0.95, 0.24], [0.85, 0.24], [1.1, 0.5], [0.6, 0.9], [-0.8, 0.92]], 1.4), team),
      colored(new THREE.BoxGeometry(0.16, 0.56, 1.4), SCIFI.white, { x: -0.74, y: 0.55 }),
      colored(new THREE.BoxGeometry(0.16, 0.56, 1.4), SCIFI.white, { x: 0.74, y: 0.55 }),
      colored(new THREE.BoxGeometry(0.18, 0.06, 1.42), SCIFI.gold, { x: -0.75, y: 0.86 }),
      colored(new THREE.BoxGeometry(0.18, 0.06, 1.42), SCIFI.gold, { x: 0.75, y: 0.86 }),
      // Long barrel between two rails, ending in a gold muzzle brake
      colored(new THREE.BoxGeometry(0.56, 0.46, 0.5), SCIFI.gunmetal, { y: 0.55, z: 1.05 }),
      colored(new THREE.CylinderGeometry(0.18, 0.21, 0.5, 8), SCIFI.alloyDark, { y: 0.55, z: 1.4, rx: Math.PI / 2 }),
      colored(new THREE.BoxGeometry(0.08, 0.34, 0.5), SCIFI.white, { x: -0.25, y: 0.55, z: 1.38 }),
      colored(new THREE.BoxGeometry(0.08, 0.34, 0.5), SCIFI.white, { x: 0.25, y: 0.55, z: 1.38 }),
      colored(new THREE.BoxGeometry(0.5, 0.36, 0.18), SCIFI.gold, { y: 0.55, z: 1.62 }),
      // Sensor dome
      colored(new THREE.SphereGeometry(0.22, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), SCIFI.alloy, { x: -0.4, y: 0.92, z: -0.25 })
    ];
    const glowParts = [
      colored(new THREE.CylinderGeometry(1.06, 1.06, 0.05, 6), trim, { y: 0.24 }),
      colored(new THREE.BoxGeometry(0.06, 0.1, 0.12), SCIFI.cyan, { x: -0.4, y: 1.0, z: -0.06 }),
      colored(new THREE.BoxGeometry(0.3, 0.14, 0.04), trim, { y: 0.55, z: 1.72 })
    ];
    for (const z of [1.34, 1.5]) {
      glowParts.push(colored(new THREE.CylinderGeometry(0.24, 0.24, 0.07, 8), trim, { y: 0.55, z, rx: Math.PI / 2 }));
    }
    return { hull, glow: glowParts };
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate body rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update body rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate body rotation
      const currentRotation = this.mesh.rotation.y;
      let rotationDiff = this.targetRotation - currentRotation;

      // Handle rotation wrapping
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;

      this.mesh.rotation.y += rotationDiff * 0.1;

      // Smoothly interpolate turret rotation (relative to body)
      if (this.turretGroup) {
        // Calculate turret rotation relative to body
        const relativeTargetRotation = this.turretTargetRotation - this.mesh.rotation.y;
        let turretDiff = relativeTargetRotation - this.turretGroup.rotation.y;

        // Handle rotation wrapping
        while (turretDiff > Math.PI) turretDiff -= Math.PI * 2;
        while (turretDiff < -Math.PI) turretDiff += Math.PI * 2;

        this.turretGroup.rotation.y += turretDiff * 0.15;
      }

      this.animate();

      // Store last position for next frame
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Hover bob and lean for the tank; a diagonal-pair gait for the walker
  animate() {
    const dt = this.gait.step(this.isSuper ? 2.5 : 4, this.isSuper ? 1.5 : 0);
    if (dt <= 0) return;
    const t = performance.now() / 1000 + this.phase;
    const walk = this.gait.walk;

    // Recoil eases back out after a shot
    this.recoil = Math.max(0, this.recoil - dt * 4);
    this.recoilGroup.position.z = -(this.isSuper ? 0.25 : 0.18) * this.recoil * this.recoil;

    if (!this.isSuper) {
      this.hoverGroup.position.y = Math.sin(t * 2.2) * 0.07;
      // Nose dips as it accelerates
      this.lean += (walk * 0.04 - this.lean) * Math.min(1, dt * 4);
      this.hoverGroup.rotation.x = this.lean + Math.sin(t * 1.3) * 0.015;
      this.wash.scale.set(0.9 + Math.sin(t * 2.2) * 0.04, 1.3 + Math.sin(t * 2.2) * 0.05, 1);
      return;
    }

    const stride = this.gait.phase;
    for (const leg of this.legs) {
      const p = stride + leg.userData.offset;
      leg.rotation.y = leg.userData.sx * Math.sin(p) * 0.25 * walk;
      // Feet lift on the forward swing
      leg.position.y = HIP_Y + Math.max(0, Math.cos(p)) * 0.3 * walk;
    }
    this.hoverGroup.position.y = Math.abs(Math.sin(stride)) * 0.06 * walk + Math.sin(t * 1.5) * 0.025 * (1 - walk);
    this.hoverGroup.rotation.z = Math.sin(stride) * 0.012 * walk;
  }

  // Called when this tank fires: kick the gun back
  fire() {
    this.recoil = 1;
  }

  // Set target position for turret to aim at
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Aim turret at a world position
  aimAt(worldX, worldZ) {
    if (this.mesh) {
      const dx = worldX - this.mesh.position.x;
      const dz = worldZ - this.mesh.position.z;
      this.turretTargetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];
  }
}
